import React from 'react';
import { format, parseISO } from 'date-fns';
import { ArrowPathIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import LearnersIcon from '../icons/LearnersIcon';
import { colorForEvent, EVENT_COLOR_CLASSES } from './eventColors';
import type { CalendarEvent } from '../../types/integrations';

interface CalendarEventPillProps {
  event: CalendarEvent;
  onClick?: (event: CalendarEvent) => void;
  variant?: 'compact' | 'block';
  showTime?: boolean;
}

const formatStart = (event: CalendarEvent): string | null => {
  if (event.all_day) return null;
  try {
    const date = parseISO(event.start);
    return format(date, 'h:mma').toLowerCase().replace(/m$/, '');
  } catch {
    return null;
  }
};

const CalendarEventPill: React.FC<CalendarEventPillProps> = ({
  event,
  onClick,
  variant = 'compact',
  showTime = true,
}) => {
  const color = EVENT_COLOR_CLASSES[colorForEvent(event.id)];
  const time = showTime ? formatStart(event) : null;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClick?.(event);
  };

  const attendeeCount = event.attendees_count ?? event.attendees?.length ?? 0;
  const hasMarkers =
    event.is_recurring || !!event.meeting_link || attendeeCount > 0;

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        'group w-full text-left rounded-1 px-2 py-1 text-xs font-medium flex items-center gap-1 overflow-hidden',
        'active:scale-[0.97] transition-transform',
        color.pill,
        event.is_tereiko ? 'border-l-2 border-primary-500' : '',
        variant === 'block' ? 'block' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      title={event.summary}
    >
      {time && <span className="font-bold shrink-0">{time}</span>}
      <span className="truncate flex-1 min-w-0">{event.summary || 'Untitled event'}</span>
      {hasMarkers && (
        <span className="inline-flex items-center gap-0.5 shrink-0 opacity-80">
          {event.is_recurring && <ArrowPathIcon className="w-3 h-3" />}
          {event.meeting_link && <VideoCameraIcon className="w-3 h-3" />}
          {attendeeCount > 0 && (
            <span className="inline-flex items-center">
              <LearnersIcon className="w-3 h-3" />
            </span>
          )}
        </span>
      )}
    </button>
  );
};

export default CalendarEventPill;
